import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Cookie } from 'lucide-react';
import SEO from '../components/ui/SEO';

export default function PrivacyPolicy() {
    const lastUpdated = "16 février 2026";

    return (
        <>
            <SEO
                title="Politique de Confidentialité | UTA Barvaux"
                description="Notre politique de confidentialité explique comment l'UTA Barvaux collecte, utilise et protège vos données personnelles conformément au RGPD."
            />

            <main className="pt-36 pb-16 bg-white min-h-screen">
                <div className="container mx-auto px-6 max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-4xl md:text-5xl font-serif font-bold text-primary-900 mb-6">
                            Politique de Confidentialité
                        </h1>
                        <p className="text-gray-500 mb-12">
                            Dernière mise à jour : {lastUpdated}
                        </p>

                        <div className="prose prose-lg text-gray-600 max-w-none">
                            <p className="lead text-xl text-primary-800 mb-8">
                                L'Université de Tous Âges Nord-Luxembourg (Barvaux-sur-Ourthe) s'engage à protéger votre vie privée et vos données personnelles.
                                Cette politique détaille nos pratiques de collecte et de traitement des données conformément au Règlement Général sur la Protection des Données (RGPD).
                            </p>

                            <section className="mb-10">
                                <div className="flex items-center gap-3 mb-4">
                                    <Shield className="text-accent-500" size={28} />
                                    <h2 className="text-2xl font-bold text-primary-900 m-0">Respect du RGPD</h2>
                                </div>
                                <p>
                                    En tant qu'association sans but lucratif opérant en Belgique, nous respectons scrupuleusement le Règlement (UE) 2016/679 (RGPD).
                                    Nous ne collectons que les données strictement nécessaires au fonctionnement de notre association et à la communication avec nos membres.
                                </p>
                            </section>

                            <section className="mb-10">
                                <div className="flex items-center gap-3 mb-4">
                                    <Eye className="text-accent-500" size={28} />
                                    <h2 className="text-2xl font-bold text-primary-900 m-0">Données collectées</h2>
                                </div>
                                <p>Nous pouvons collecter les informations suivantes lorsque vous interagissez avec notre site ou devenez membre :</p>
                                <ul className="list-disc pl-6 space-y-2 mt-4">
                                    <li><strong>Informations d'identification</strong> : Nom, prénom, adresse postale.</li>
                                    <li><strong>Coordonnées</strong> : Adresse e-mail, numéro de téléphone.</li>
                                    <li><strong>Données de connexion</strong> : Lors de l'utilisation de notre formulaire de contact (protégé par Google reCAPTCHA) ou de l'administration.</li>
                                </ul>
                            </section>

                            <section className="mb-10">
                                <div className="flex items-center gap-3 mb-4">
                                    <Lock className="text-accent-500" size={28} />
                                    <h2 className="text-2xl font-bold text-primary-900 m-0">Utilisation de vos données</h2>
                                </div>
                                <p>Vos données sont utilisées exclusivement pour :</p>
                                <ul className="list-disc pl-6 space-y-2 mt-4">
                                    <li>La gestion de votre adhésion et de vos abonnements.</li>
                                    <li>Vous informer de nos activités, conférences et changements de programme.</li>
                                    <li>Répondre à vos demandes via le formulaire de contact.</li>
                                    <li>Assurer la sécurité de notre site web.</li>
                                </ul>
                                <p className="mt-4 font-bold text-primary-900">
                                    Nous ne vendons, n'échangeons ni ne louons jamais vos données personnelles à des tiers.
                                </p>
                            </section>

                            <section className="mb-10">
                                <div className="flex items-center gap-3 mb-4">
                                    <Cookie className="text-accent-500" size={28} />
                                    <h2 className="text-2xl font-bold text-primary-900 m-0">Cookies et Technologies</h2>
                                </div>
                                <p>
                                    Notre site utilise des cookies essentiels pour son fonctionnement technique et pour la sécurité (notamment via Google reCAPTCHA).
                                    Nous n'utilisons pas de cookies publicitaires ou de traçage commercial intrusif.
                                </p>
                            </section>

                            <section className="mb-12">
                                <h2 className="text-2xl font-bold text-primary-900 mb-4">Vos droits</h2>
                                <p>Conformément au RGPD, vous disposez des droits suivants :</p>
                                <ul className="list-disc pl-6 space-y-2 mt-4">
                                    <li>Droit d'accès à vos données.</li>
                                    <li>Droit de rectification de vos données.</li>
                                    <li>Droit à l'effacement ("droit à l'oubli").</li>
                                    <li>Droit à la limitation du traitement.</li>
                                </ul>
                                <p className="mt-6">
                                    Pour exercer ces droits, contactez-nous à : <a href="mailto:daniellemottet47@gmail.com" className="text-accent-600 font-bold hover:underline">daniellemottet47@gmail.com</a>
                                </p>
                            </section>
                        </div>
                    </motion.div>
                </div>
            </main>
        </>
    );
}
